// src/components/nir/Form.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Form, Row, Col, Table, Button, InputGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { format, parseISO, isValid as isValidDate } from "date-fns";
import { AsyncPaginate } from "react-select-async-paginate";
import { components } from "react-select";
import { authFetch } from "../utils/authFetch";
import SeriiModal from "./SeriiModal";

// --- date helpers ---
const toYMD = (d) => (d && isValidDate(d) ? format(d, "yyyy-MM-dd") : "");
const fromISOToDate = (iso) => {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  try {
    return parseISO(iso);
  } catch {
    return null;
  }
};
const ymdToISODateTime = (ymd, hh = "00", mm = "00") =>
  ymd ? `${ymd}T${hh}:${mm}:00` : "";

const LIMIT = 20;
const num = (v, def = 0) =>
  v === "" || v == null || Number.isNaN(Number(v)) ? def : Number(v);

export function useEmptyNir() {
  const todayISO = ymdToISODateTime(
    format(new Date(), "yyyy-MM-dd"),
    "00",
    "00",
  );
  return {
    bxdoc_no: "",
    bxdoc_date: todayISO,
    bxgestiune_id: 0,
    contact_id: 0,
    observatii: "",
    linii: [
      {
        bxpart_id: 0,
        bxqty: 1,
        bxpart_regprice: 0,
        observatii: "",
        line_amount: 0,
        line_vat: 0,
        line_total: 0,
      },
    ],
    ftva: 0,
    tva: 0,
    total: 0,
  };
}

export function NirForm({ value, onChange, onSubmit, submitting, showActions = true }) {
  const v = value || {};
  const linii = v.linii || [];
  const receiptValue = linii.reduce(
    (sum, line) => sum + num(line.bxqty) * num(line.bxpart_regprice), 0,
  );
  const isFinalized = !!v.bxfinal_bool;
  const isFromIntrare = !!v.intrare_id || !!v.source_intrare_id;

  // select state
  const [selContact, setSelContact] = useState(null);
  const [cuSerii, setCuSerii] = useState(false);
  const [seriiPicker, setSeriiPicker] = useState({ show: false, row: null });
  const [selGestiune, setSelGestiune] = useState(null);
  const [selPartByIdx, setSelPartByIdx] = useState({}); // { idx: { value, label, um_id, um_label } }

  // caches
  const partCache = useRef(new Map());
  const umCache = useRef(new Map());

  useEffect(() => {
    let alive = true;
    const fetchFirma = async () => {
      try {
        const res = await authFetch("/firma/");
        const items = res?.items || res || [];
        if (items.length > 0 && alive) {
          setCuSerii(items[0].cu_serii === 1);
        }
      } catch {}
    };
    fetchFirma();
    return () => { alive = false; };
  }, []);


  // preload master selects on edit
  useEffect(() => {
    let alive = true;
    const preload = async () => {
      if (v?.contact_id) {
        try {
          const c = await authFetch(`/contacte/${v.contact_id}`);
          if (alive)
            setSelContact({
              value: c.id,
              label: `${c.bxdenumire}${c.bxcodfiscal ? ` (${c.bxcodfiscal})` : ""}`,
            });
        } catch {
          if (alive)
            setSelContact({
              value: v.contact_id,
              label: `Contact #${v.contact_id}`,
            });
        }
      } else setSelContact(null);

      if (v?.bxgestiune_id) {
        const candidates = [`/bxgestiune/${v.bxgestiune_id}`];
        console.log(candidates);
        let g = null;
        for (const url of candidates) {
          try {
            g = await authFetch(url);
            break;
          } catch {}
        }
        if (alive) {
          if (g)
            setSelGestiune({
              value: g.id,
              label:
                `${g.bxcod ?? g.cod ?? g.code ?? g.id} — ${g.bxnume ?? g.denumire ?? g.bxdenumire ?? ""}`.trim(),
            });
          else
            setSelGestiune({
              value: v.bxgestiune_id,
              label: `Gestiune #${v.bxgestiune_id}`,
            });
        }
      } else setSelGestiune(null);
    };
    preload();
    return () => {
      alive = false;
    };
  }, [v?.contact_id, v?.bxgestiune_id]);

  // helper: get UM label by id (for display only)
  const getUmLabel = useCallback(async (umId) => {
    if (!umId) return "";
    let u = umCache.current.get(umId);
    if (!u) {
      try {
        u = await authFetch(`/bxum/${umId}`);
        if (u?.id) umCache.current.set(u.id, u);
      } catch {
        /* ignore */
      }
    }
    return u ? `${u.cod ?? u.code ?? u.id}` : `#${umId}`;
  }, []);

  // preload per-line: part -> derive UM label (display only)
  useEffect(() => {
    let alive = true;
    const ensurePart = async (idx, id) => {
      if (!id) {
        setSelPartByIdx((m) => {
          const cp = { ...m };
          delete cp[idx];
          return cp;
        });
        return;
      }
      if (selPartByIdx[idx]?.value === id && selPartByIdx[idx]?.um_label)
        return;
      let p = partCache.current.get(id);
      if (!p) {
        try {
          p = await authFetch(`/bxpart/${id}`);
          if (p?.id) partCache.current.set(p.id, p);
        } catch {}
      }
      const um_id = p?.um_id ?? null;
      const um_label = um_id ? await getUmLabel(um_id) : "";
      if (!alive) return;
      setSelPartByIdx((m) => ({
        ...m,
        [idx]: {
          value: id,
          label: `${p?.cod ?? ""} — ${p?.denumire ?? ""}`.trim(),
          um_id,
          um_label,
        },
      }));
    };
    (linii || []).forEach((ln, idx) => {
      if (ln?.bxpart_id) ensurePart(idx, ln.bxpart_id);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linii]);

  // react-select highlighted option
  const HighlightedOption = (props) => {
    const q = (props.selectProps.inputValue || "").trim();
    const label = props.data.label || "";
    const parts = q ? label.split(new RegExp(`(${q})`, "i")) : [label];
    return (
      <components.Option {...props}>
        {parts.map((p, i) =>
          q && p.toLowerCase() === q.toLowerCase() ? (
            <mark key={i}>{p}</mark>
          ) : (
            <span key={i}>{p}</span>
          ),
        )}
      </components.Option>
    );
  };

  // generic paged loader
  const loadPaged = async (url, q, page, limit) => {
    const skip = (page - 1) * limit;
    const sep = url.includes("?") ? "&" : "?";
    const full = `${url}${sep}search=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`;
    const data = await authFetch(full);
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
        ? data.items
        : [];
    const total =
      typeof data?.total === "number"
        ? data.total
        : typeof data?.count === "number"
          ? data.count
          : undefined;
    const hasMore =
      typeof total === "number"
        ? skip + items.length < total
        : items.length === limit;
    return { items, hasMore };
  };

  // loaders
  const loadContactOptions = async (inputValue, _loaded, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    try {
      const { items, hasMore } = await loadPaged("/contacte/", q, page, LIMIT);
      const options = items.map((c) => ({
        value: c.id,
        label: `${c.bxdenumire}${c.bxcodfiscal ? ` (${c.bxcodfiscal})` : ""}`,
      }));
      return { options, hasMore, additional: { page: page + 1 } };
    } catch {
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  const loadGestiuneOptions = async (inputValue, _l, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    const bases = ["/gestiune/", "/gestiuni/", "/bxgestiune/"];
    for (const base of bases) {
      try {
        const { items, hasMore } = await loadPaged(base, q, page, LIMIT);
        const options = items.map((g) => ({
          value: g.id,
          label:
            `${g.bxcod ?? g.cod ?? g.code ?? g.id} — ${g.bxnume ?? g.denumire ?? g.bxdenumire ?? ""}`.trim(),
        }));
        return { options, hasMore, additional: { page: page + 1 } };
      } catch {}
    }
    return { options: [], hasMore: false, additional: { page } };
  };

  const loadPartOptions = async (inputValue, _l, additional) => {
    const page = additional?.page ?? 1;
    const q = (inputValue || "").trim();
    try {
      const { items, hasMore } = await loadPaged("/bxpart/", q, page, LIMIT);
      items.forEach((it) => partCache.current.set(it.id, it));
      const options = items.map((p) => ({
        value: p.id,
        label: `${p.cod ?? ""} — ${p.denumire ?? ""}`.trim(),
        um_id: p.um_id ?? null,
      }));
      return { options, hasMore, additional: { page: page + 1 } };
    } catch {
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  // master + line logic
  const updateMaster = (patch) => {
    const m = { ...v, ...patch };
    const ftva = (m.linii || []).reduce((s, ln) => s + num(ln.line_amount), 0);
    const tva = 0.0;
    const total = +ftva.toFixed(2);
    onChange({ ...m, ftva: +ftva.toFixed(2), tva, total });
  };

  const setBxdocDate = (d) =>
    updateMaster({ bxdoc_date: ymdToISODateTime(toYMD(d), "00", "00") });

  const handleContactChange = (opt) => {
    setSelContact(opt);
    updateMaster({ contact_id: opt ? opt.value : 0 });
  };
  const handleGestiuneChange = (opt) => {
    setSelGestiune(opt);
    updateMaster({ bxgestiune_id: opt ? opt.value : 0 });
  };

  const calcLine = (line) => {
    const qty = num(line.bxqty);
    const price = num(line.bxpart_regprice);
    const amount = +(qty * price).toFixed(2);
    const vat = 0.0;
    const total = amount;
    return { ...line, line_amount: amount, line_vat: vat, line_total: total };
  };

  const updateLine = (idx, patch) => {
    const lns = linii.map((ln, i) =>
      i === idx ? calcLine({ ...ln, ...patch }) : ln,
    );
    updateMaster({ linii: lns });
  };

  const handlePartChange = async (idx, opt) => {
    const partId = opt ? opt.value : 0;
    let um_label = "";
    if (opt?.um_id) um_label = await getUmLabel(opt.um_id);
    setSelPartByIdx((m) => ({
      ...m,
      [idx]: opt ? { ...opt, um_label } : undefined,
    }));
    updateLine(idx, { bxpart_id: partId });
  };

  const addLine = () => {
    const first = linii[0] || {};
    const newLn = calcLine({
      ...first,
      bxpart_id: 0,
      bxqty: 1,
      bxpart_regprice: 0,
      observatii: "",
    });
    updateMaster({ linii: [...linii, newLn] });
  };

  const removeLine = (idx) =>
    updateMaster({ linii: linii.filter((_, i) => i !== idx) });

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit?.();
      }}
    >
      <Row className="g-3">
        <Col md={3}>
          <Form.Group controlId="bxdoc_no">
            <Form.Label>Nr. document</Form.Label>
            <Form.Control
              value={v.bxdoc_no || ""}
              onChange={(e) => updateMaster({ bxdoc_no: e.target.value })}
              disabled={isFinalized || isFromIntrare}
            />
          </Form.Group>
        </Col>

        <Col md="auto">
          <Form.Group controlId="bxdoc_date">
            <Form.Label>Data document</Form.Label>
            <InputGroup size="sm" className="w-auto">
              <DatePicker
                selected={fromISOToDate(v.bxdoc_date)}
                onChange={setBxdocDate}
                dateFormat="dd.MM.yyyy"
                isClearable
                placeholderText="zz.ll.aaaa"
                className="form-control form-control-sm"
                wrapperClassName="dp-w120"
                disabled={isFinalized || isFromIntrare}
              />
            </InputGroup>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="bxgestiune_id">
            <Form.Label>Gestiune</Form.Label>
            <AsyncPaginate
              value={selGestiune}
              loadOptions={loadGestiuneOptions}
              onChange={handleGestiuneChange}
              isClearable
              placeholder="Cauta gestiune..."
              debounceTimeout={300}
              cacheOptions
              defaultOptions
              openMenuOnFocus
              additional={{ page: 1 }}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              components={{ Option: HighlightedOption }}
              isDisabled={isFinalized}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="contact_id">
            <Form.Label>Furnizor</Form.Label>
            <AsyncPaginate
              value={selContact}
              loadOptions={loadContactOptions}
              onChange={handleContactChange}
              isClearable
              placeholder="Cauta furnizor (nume/CUI)…"
              debounceTimeout={300}
              cacheOptions
              defaultOptions
              openMenuOnFocus
              additional={{ page: 1 }}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              components={{ Option: HighlightedOption }}
              isDisabled={isFinalized || isFromIntrare}
            />
          </Form.Group>
        </Col>
      </Row>

      <hr className="my-4" />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Linii</h5>
        {!isFinalized && !isFromIntrare && (
          <Button variant="outline-secondary" size="sm" onClick={addLine}>
            + Linie
          </Button>
        )}
      </div>

      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th>Articol</th>
            <th>UM</th>
            <th>Cant.</th>
            <th>Pret</th>
            <th>Valoare</th>
            <th>Observatii</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {linii.map((ln, idx) => (
            <tr key={idx}>
              <td style={{ minWidth: 260 }}>
                <AsyncPaginate
                  value={selPartByIdx[idx] || null}
                  loadOptions={loadPartOptions}
                  onChange={(opt) => handlePartChange(idx, opt)}
                  isClearable
                  placeholder="Cauta articol..."
                  debounceTimeout={300}
                  cacheOptions
                  defaultOptions
                  additional={{ page: 1 }}
                  components={{ Option: HighlightedOption }}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  isDisabled={isFinalized || isFromIntrare}
                />
              </td>

              {/* UM read-only, derivat din part */}
              <td style={{ minWidth: 100, verticalAlign: "middle" }}>
                <span className="text-muted">
                  {selPartByIdx[idx]?.um_label || ""}
                </span>
              </td>

              <td style={{ width: 120 }}>
                <Form.Control
                  type="number"
                  step="0.001"
                  value={ln.bxqty}
                  onChange={(e) =>
                    updateLine(idx, { bxqty: num(e.target.value) })
                  }
                  disabled={isFinalized || isFromIntrare}
                />
              </td>
              <td style={{ width: 120 }}>
                <Form.Control
                  type="number"
                  step="0.0001"
                  value={ln.bxpart_regprice}
                  onChange={(e) =>
                    updateLine(idx, { bxpart_regprice: num(e.target.value) })
                  }
                  disabled={isFinalized || isFromIntrare}
                />
              </td>
              <td style={{ width: 110 }}>
                {(ln.bxpart_regprice * ln.bxqty ?? 0).toFixed(2)}
              </td>
                              <td style={{ minWidth: 150 }}>
                  <InputGroup size="sm">
                    <Form.Control
                        type="text"
                        placeholder="Observatii linie..."
                        value={ln.observatii || ""}
                        onChange={(e) =>
                        updateLine(idx, { observatii: e.target.value })
                        }
                        disabled={isFinalized}
                    />
                    
                  </InputGroup>
                </td>
              {/* <td style={{ width: 110 }}>{(ln.line_vat ?? 0).toFixed(2)}</td>
              <td style={{ width: 110 }}>{(ln.line_total ?? 0).toFixed(2)}</td> */}
              <td className="text-end" style={{ width: cuSerii ? 170 : 80 }}>
                  <div className="d-flex flex-nowrap justify-content-end align-items-center gap-2">
                    {!isFinalized && !isFromIntrare && (
                      <Button
                        variant="link"
                        className="text-danger p-0 m-0 text-decoration-none"
                        onClick={() => {
                          const lns = linii.filter((_, i) => i !== idx);
                          const partSel = { ...selPartByIdx };
                          delete partSel[idx];
                          setSelPartByIdx(partSel);
                          updateMaster({ linii: lns });
                        }}
                      >
                        Sterge
                      </Button>
                    )}
                    {cuSerii && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        className="m-0"
                        style={{ whiteSpace: "nowrap" }} onClick={() => setSeriiPicker({show: true, row: idx})}
                      >
                        Serii ({ln.serii?.length || 0})
                      </Button>
                    )}
                  </div>
                </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center border-top pt-3">
        <div className="text-muted">
          Total fara TVA: <strong>{receiptValue.toFixed(2)}</strong> |
          TVA: <strong>{Number(v.tva || 0).toFixed(2)}</strong> | Total:{" "}
          <strong>{(receiptValue + num(v.tva)).toFixed(2)}</strong>
        </div>
        <div>
          {showActions && !isFinalized && (
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Se salveaza…" : "Salveaza"}
            </Button>
          )}
        </div>
      </div>

      <SeriiModal
        readOnly={isFinalized}
        show={seriiPicker.show}
        onHide={() => setSeriiPicker({ show: false, row: null })}
        line={seriiPicker.row !== null ? linii[seriiPicker.row] : null}
        onSave={(serii) => {
          if (seriiPicker.row !== null) {
            updateLine(seriiPicker.row, { serii });
          }
        }}
      />
    </Form>
  );
}
